import { useAppSelector } from "store";
import { isFakeEnterprisePackage, isModuleEnabled, ModuleKey } from "types/permissions";

interface PermissionState {
  isLoading: boolean;
  /**
   * `permissions` is unresolved AND the last `GET /v1/users/{id}` failed. A third outcome
   * next to loading/resolved: render a retry state, never Contact Us — an unresolved
   * permission is not a denied one (`[PERMISSION_LOADING_FALLBACK]`). `isLoading` stays
   * true alongside it so existing "still loading" guards keep holding.
   */
  isError: boolean;
  isEnabled: boolean;
  isVisible: boolean;
  isDisabled: boolean;
}

/**
 * Hook to get consistent permission state for a module.
 *
 * @param moduleKey - The module key to check (e.g., "design", "photoshoot")
 * @returns PermissionState object with loading, enabled, visible, and disabled states
 *
 * @example
 * const { isLoading, isEnabled, isVisible, isDisabled } = usePermissionState("design");
 * const photoshoot = usePermissionState("photoshoot");
 */
const usePermissionState = (moduleKey: ModuleKey): PermissionState => {
  const permissions = useAppSelector((state) => state.common.user.permissions);
  const packageCode = useAppSelector((state) => state.common.user.packageCode);
  const permissionsError = useAppSelector((state) => state.common.permissionsError);

  if (!permissions) {
    return {
      isLoading: true,
      isError: permissionsError,
      isEnabled: false,
      isVisible: true,
      isDisabled: false,
    };
  }

  if (isFakeEnterprisePackage(packageCode)) {
    return {
      isLoading: false,
      isError: false,
      isEnabled: false,
      isVisible: true,
      isDisabled: true,
    };
  }

  const permission = permissions[moduleKey];

  return {
    isLoading: false,
    isError: false,
    // Shared with the route guard and the chatbot's guided-offer gate (`types/permissions.ts`) so
    // "does this user have this module" has exactly one definition.
    isEnabled: isModuleEnabled(moduleKey, permissions, packageCode),
    isVisible: permission?.is_visible !== false,
    isDisabled: permission?.is_enabled === false,
  };
};

export default usePermissionState;
